(() => {
  "use strict";

  const STORAGE_KEY = "squish-club-shopify-builder-v2";
  const MILESTONES = [2, 3, 5, 8];
  const TRAY_SIZE = 6;
  const MAX_COMPONENTS = 8;
  const FIXTURE_SIZES = new Set([18, 50, 100, 250]);
  const INLINE_IMAGE_PLACEHOLDER =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 3'%3E%3Crect width='4' height='3' fill='%23f5eadb'/%3E%3C/svg%3E";

  const parseJson = (element, fallback) => {
    try {
      return JSON.parse(element?.textContent || "");
    } catch {
      return fallback;
    }
  };

  const escapeHtml = (value) =>
    String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const normalizeCategoryLabel = (value) => String(value || "").trim().replace(/\s+/g, " ");
  const categoryKey = (value) => normalizeCategoryLabel(value).toLocaleLowerCase("en-US");
  const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const fetchWithTimeout = async (url, options = {}, timeoutMilliseconds = 8_000) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMilliseconds);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } catch (error) {
      if (error?.name === "AbortError") {
        const timeoutError = new Error("The request took too long. Check your connection and try again.");
        timeoutError.name = "TimeoutError";
        throw timeoutError;
      }
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const readShopifyJson = async (response, fallbackMessage) => {
    const contentType = response.headers.get("Content-Type") || "";
    const normalizedContentType = contentType.toLocaleLowerCase("en-US");
    if (!normalizedContentType.includes("json") && !normalizedContentType.includes("javascript")) {
      throw new Error(
        response.status === 429 || normalizedContentType.includes("html")
          ? "Shopify needs to verify this connection. Refresh the page, then try again."
          : fallbackMessage,
      );
    }
    try {
      return await response.json();
    } catch {
      throw new Error(fallbackMessage);
    }
  };

  const loadRemainingCatalogPages = async (root, firstPageProducts) => {
    const pageCount = Math.max(1, Number(root.dataset.catalogPages) || 1);
    if (pageCount <= 1) return firstPageProducts;
    const sectionId = root.dataset.sectionId;
    if (!sectionId) return firstPageProducts;

    const requests = [];
    for (let page = 2; page <= pageCount; page += 1) {
      const url = new URL(window.location.href);
      url.searchParams.set("page", String(page));
      url.searchParams.set("section_id", sectionId);
      requests.push(fetchWithTimeout(url, { headers: { Accept: "text/html" } }, 3_500).then(async (response) => {
        if (!response.ok) throw new Error(`Catalog page ${page} failed (${response.status})`);
        const documentFragment = new DOMParser().parseFromString(await response.text(), "text/html");
        return parseJson(documentFragment.querySelector("[data-sc-products]"), []);
      }));
    }

    try {
      const pages = await Promise.all(requests);
      const unique = new Map();
      for (const product of [firstPageProducts, ...pages].flat()) {
        if (product?.id && !unique.has(product.id)) unique.set(product.id, product);
      }
      return [...unique.values()];
    } catch (error) {
      console.warn("Squish Club loaded the first catalog page only.", error);
      return firstPageProducts;
    }
  };

  const fetchCartSnapshot = async (url) => {
    let response;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        response = await fetchWithTimeout(url, { headers: { Accept: "application/json" } }, 5_500);
      } catch (error) {
        if (attempt === 1) throw error;
        await delay(500);
        continue;
      }
      if (response.ok || ![429, 502, 503, 504].includes(response.status) || attempt === 1) return response;
      const retryAfterSeconds = Number.parseFloat(response.headers.get("Retry-After") || "0");
      await delay(Math.min(1_500, Math.max(500, retryAfterSeconds * 1_000 || 500)));
    }
    return response;
  };

  const shuffled = (items) => {
    const copy = [...items];
    for (let index = copy.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
    }
    return copy;
  };

  const readStoredState = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    } catch {
      return null;
    }
  };

  const withFixtureQuery = (value, index) => {
    if (!value) return value;
    try {
      const url = new URL(value, window.location.href);
      url.searchParams.set("sc_fixture_asset", String(index));
      return url.href;
    } catch {
      return value;
    }
  };

  const expandFixtureProducts = (products, requestedSize) => {
    if (!FIXTURE_SIZES.has(requestedSize) || requestedSize <= products.length) return products;
    const expanded = [...products];
    for (let index = products.length; index < requestedSize; index += 1) {
      const source = products[index % products.length];
      const fixtureNumber = index + 1;
      expanded.push({
        ...source,
        id: `${source.id}--fixture-${fixtureNumber}`,
        name: `${source.name} · Test ${fixtureNumber}`,
        image320: withFixtureQuery(source.image320, fixtureNumber),
        image480: withFixtureQuery(source.image480, fixtureNumber),
        image720: withFixtureQuery(source.image720, fixtureNumber),
        image1000: withFixtureQuery(source.image1000, fixtureNumber),
        fixture: true,
      });
    }
    return expanded;
  };

  function createPerformanceTracker(root, panel, context) {
    const enabled = context.params.get("sc_perf") === "1";
    const state = {
      version: "squish-club-media-perf-v1",
      enabled,
      fixtureSize: context.fixtureSize,
      productCount: context.productCount,
      events: [],
      largestContentfulPaint: 0,
      largestContentfulPaintElement: null,
      cumulativeLayoutShift: 0,
      longTaskTime: 0,
      videoRequestsBeforeReady: null,
      pickerReadyAt: null,
      lastShuffleDuration: null,
      lastVideoFirstFrame: null,
    };

    let panelFrame;
    const videoResources = () =>
      performance
        .getEntriesByType("resource")
        .filter((entry) => /\/videos\/|\.m3u8(?:\?|$)|\.mp4(?:\?|$)/i.test(entry.name));

    const imageResources = () =>
      performance
        .getEntriesByType("resource")
        .filter((entry) => entry.initiatorType === "img" || /cdn\.shopify\.com.*\.(png|jpe?g|webp|avif)/i.test(entry.name));

    const bytesFor = (entries) =>
      entries.reduce((sum, entry) => sum + (entry.transferSize || entry.encodedBodySize || 0), 0);

    const navigationSnapshot = () => {
      const navigation = performance.getEntriesByType("navigation")[0];
      if (!navigation) return {};
      return {
        ttfb: Math.round(navigation.responseStart),
        domInteractive: Math.round(navigation.domInteractive),
        domContentLoaded: Math.round(navigation.domContentLoadedEventEnd),
        load: navigation.loadEventEnd ? Math.round(navigation.loadEventEnd) : null,
        transferredBytes: navigation.transferSize || 0,
      };
    };

    const getSnapshot = () => {
      const images = imageResources();
      const videos = videoResources();
      return {
        ...state,
        events: [...state.events],
        navigation: navigationSnapshot(),
        resources: {
          total: performance.getEntriesByType("resource").length,
          images: images.length,
          imageBytes: bytesFor(images),
          videos: videos.length,
          videoBytes: bytesFor(videos),
          videoUrls: videos.map((entry) => entry.name),
        },
        connection: {
          effectiveType: navigator.connection?.effectiveType || "unknown",
          saveData: Boolean(navigator.connection?.saveData),
        },
      };
    };

    const renderPanel = () => {
      if (!enabled || !panel) return;
      window.cancelAnimationFrame(panelFrame);
      panelFrame = window.requestAnimationFrame(() => {
        const snapshot = getSnapshot();
        const latestEvent = snapshot.events.at(-1)?.name || "initializing";
        panel.hidden = false;
        panel.innerHTML = `
          <strong>MEDIA PERFORMANCE TEST</strong>
          <dl>
            <dt>Products</dt><dd>${snapshot.productCount}</dd>
            <dt>Fixture</dt><dd>${snapshot.fixtureSize || "off"}</dd>
            <dt>Picker ready</dt><dd>${snapshot.pickerReadyAt == null ? "…" : `${snapshot.pickerReadyAt} ms`}</dd>
            <dt>LCP</dt><dd>${snapshot.largestContentfulPaint ? `${snapshot.largestContentfulPaint} ms` : "…"}</dd>
            <dt>CLS</dt><dd>${snapshot.cumulativeLayoutShift.toFixed(3)}</dd>
            <dt>Long tasks</dt><dd>${Math.round(snapshot.longTaskTime)} ms</dd>
            <dt>Images</dt><dd>${snapshot.resources.images}</dd>
            <dt>Image transfer</dt><dd>${Math.round(snapshot.resources.imageBytes / 1024)} KB</dd>
            <dt>Videos</dt><dd>${snapshot.resources.videos}</dd>
            <dt>Video before ready</dt><dd>${snapshot.videoRequestsBeforeReady ?? "…"}</dd>
            <dt>Last shuffle</dt><dd>${snapshot.lastShuffleDuration == null ? "—" : `${snapshot.lastShuffleDuration} ms`}</dd>
            <dt>Video first frame</dt><dd>${snapshot.lastVideoFirstFrame == null ? "—" : `${snapshot.lastVideoFirstFrame} ms`}</dd>
            <dt>Latest</dt><dd>${escapeHtml(latestEvent)}</dd>
          </dl>`;
      });
    };

    const record = (name, detail = {}) => {
      const at = Math.round(performance.now() * 10) / 10;
      state.events.push({ name, at, ...detail });
      if (state.events.length > 100) state.events.shift();
      try {
        performance.mark(name, { detail });
      } catch {
        performance.mark(name);
      }
      renderPanel();
      return at;
    };

    try {
      new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const latest = entries.at(-1);
        if (latest) {
          state.largestContentfulPaint = Math.round(latest.startTime);
          state.largestContentfulPaintElement = latest.element
            ? {
                tag: latest.element.tagName,
                id: latest.element.id || "",
                className: typeof latest.element.className === "string" ? latest.element.className : "",
                text: String(latest.element.textContent || "").trim().slice(0, 120),
                url: latest.url || "",
              }
            : null;
        }
        renderPanel();
      }).observe({ type: "largest-contentful-paint", buffered: true });
    } catch {
      // Some older browsers do not expose LCP.
    }

    try {
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) state.cumulativeLayoutShift += entry.value;
        }
        renderPanel();
      }).observe({ type: "layout-shift", buffered: true });
    } catch {
      // Layout shift observation is optional.
    }

    try {
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) state.longTaskTime += entry.duration;
        renderPanel();
      }).observe({ type: "longtask", buffered: true });
    } catch {
      // Long-task observation is optional.
    }

    const api = {
      state,
      record,
      getSnapshot,
      markPickerReady() {
        state.pickerReadyAt = Math.round(performance.now());
        state.videoRequestsBeforeReady = videoResources().length;
        record("sc_picker_ready", { videoRequestsBeforeReady: state.videoRequestsBeforeReady });
      },
      markShuffle(duration) {
        state.lastShuffleDuration = Math.round(duration * 10) / 10;
        record("sc_shuffle_rendered", { duration: state.lastShuffleDuration });
      },
      markVideoFirstFrame(duration, productId) {
        state.lastVideoFirstFrame = Math.round(duration * 10) / 10;
        record("sc_media_first_frame", { duration: state.lastVideoFirstFrame, productId });
      },
      renderPanel,
    };

    window.__SC_PERF__ = api;
    record("sc_init_start", { sectionId: root.dataset.sectionId });
    window.addEventListener("load", () => record("sc_window_load"), { once: true });
    return api;
  }

  async function initializeBuilder(root) {
    if (!root || root.dataset.scPerfReady === "true" || root.dataset.scPerfLoading === "true") return;
    root.dataset.scPerfLoading = "true";
    root.dataset.scInteractive = "false";
    root.setAttribute("aria-busy", "true");

    try {
      await initializeBuilderContents(root);
    } catch (error) {
      console.error("Squish Club could not initialize.", error);
      delete root.dataset.scPerfLoading;
      root.dataset.scInteractive = "error";
      root.setAttribute("aria-busy", "false");
      const addCart = root.querySelector("[data-sc-add-cart]");
      const label = addCart?.querySelector("span");
      if (addCart) addCart.disabled = true;
      if (label) label.textContent = "Refresh to retry";
      const toast = root.querySelector("[data-sc-toast]");
      if (toast) {
        toast.textContent = error?.message || "The picker could not load. Refresh the page and try again.";
        toast.classList.add("is-visible");
      }
    }
  }

  async function initializeBuilderContents(root) {

    const params = new URLSearchParams(window.location.search);
    if (params.get("sc_reset") === "1") {
      localStorage.removeItem(STORAGE_KEY);
      // Consume the entry-only reset. Keep product and ad attribution parameters
      // so refreshing this same visit restores subsequent selections.
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("sc_reset");
      window.history.replaceState(window.history.state, "", cleanUrl);
    }

    const firstPageProducts = parseJson(root.querySelector("[data-sc-products]"), []);
    const rawProducts = await loadRemainingCatalogPages(root, firstPageProducts);
    const configuredCategoryOrder = parseJson(root.querySelector("[data-sc-category-order]"), []);
    const bundle = parseJson(root.querySelector("[data-sc-bundle]"), null);
    const availableProducts = rawProducts.filter((product) => product.available !== false);
    if (!availableProducts.length || !bundle?.variants?.length) {
      throw new Error("The picker is temporarily unavailable. Refresh the page and try again.");
    }

    const requestedFixture = Number(params.get("sc_fixture"));
    const fixtureSize = FIXTURE_SIZES.has(requestedFixture) ? requestedFixture : 0;
    const fixtureMode = fixtureSize > availableProducts.length;

    const normalizedProducts = availableProducts.map((product) => {
      const sourceCategories = Array.isArray(product.categories) ? product.categories : [product.category];
      const categories = [...new Set(sourceCategories.map(normalizeCategoryLabel).filter(Boolean))];
      const safeCategories = categories.length ? categories : ["Calm"];
      const cardName = String(product.cardName || product.name || "")
        .replace(/\s+Squishy$/i, "")
        .trim();
      return {
        ...product,
        cardName,
        categories: safeCategories,
        categoryKeys: safeCategories.map(categoryKey),
      };
    });
    const products = expandFixtureProducts(normalizedProducts, fixtureSize);

    document.body.classList.add("sc-builder-page");

    const productById = Object.fromEntries(products.map((product) => [product.id, product]));
    const baseProductById = Object.fromEntries(normalizedProducts.map((product) => [product.id, product]));
    const variantByCount = Object.fromEntries(bundle.variants.map((variant) => [Number(variant.count), variant]));
    const allProductIds = products.map((product) => product.id);

    const actualCategoryLabels = new Map();
    products.forEach((product) => {
      product.categories.forEach((label) => {
        const key = categoryKey(label);
        if (key && !actualCategoryLabels.has(key)) actualCategoryLabels.set(key, label);
      });
    });
    const configuredLabels = new Map();
    configuredCategoryOrder.map(normalizeCategoryLabel).filter(Boolean).forEach((label) => {
      configuredLabels.set(categoryKey(label), label);
    });
    const orderedCategoryKeys = [...configuredLabels.keys(), ...actualCategoryLabels.keys()].filter(
      (key, index, list) => actualCategoryLabels.has(key) && list.indexOf(key) === index,
    );
    const categories = [
      { key: "all", label: "All" },
      ...orderedCategoryKeys.map((key) => ({
        key,
        label: configuredLabels.get(key) || actualCategoryLabels.get(key),
      })),
    ];
    const validCategoryKeys = new Set(categories.map((category) => category.key));
    const categoryPoolIds = (key) =>
      key === "all"
        ? allProductIds
        : products.filter((product) => product.categoryKeys.includes(key)).map((product) => product.id);

    const urlPick = params.get("pick");
    const stored = fixtureMode ? null : readStoredState();
    const defaultStarter = baseProductById[root.dataset.starterHandle]
      ? root.dataset.starterHandle
      : normalizedProducts[0].id;
    const pinnedId = baseProductById[urlPick]
      ? urlPick
      : baseProductById[stored?.pinnedId]
        ? stored.pinnedId
        : defaultStarter;

    const state = {
      pinnedId,
      quantities: { [pinnedId]: 1 },
      order: [pinnedId],
      category: "all",
      trayIds: [pinnedId, ...allProductIds.filter((id) => id !== pinnedId)].slice(0, TRAY_SIZE),
    };

    if (stored?.pinnedId === pinnedId) {
      const validOrder = Array.isArray(stored.order)
        ? stored.order.filter((id, index, list) => baseProductById[id] && list.indexOf(id) === index)
        : [];
      const restoredQuantities = {};
      let restoredTotal = 0;
      for (const id of [pinnedId, ...validOrder.filter((id) => id !== pinnedId)]) {
        const requested = Math.max(0, Math.floor(Number(stored.quantities?.[id] || 0)));
        const minimum = id === pinnedId ? 1 : 0;
        const quantity = Math.min(Math.max(requested, minimum), MAX_COMPONENTS - restoredTotal);
        if (quantity > 0) {
          restoredQuantities[id] = quantity;
          restoredTotal += quantity;
        }
        if (restoredTotal >= MAX_COMPONENTS) break;
      }
      state.quantities = restoredQuantities;
      state.order = [pinnedId, ...validOrder.filter((id) => id !== pinnedId && restoredQuantities[id])];
      const storedCategory = categoryKey(stored.category);
      state.category = validCategoryKeys.has(storedCategory) ? storedCategory : "all";
      const poolIds = categoryPoolIds(state.category);
      const restoredTray = Array.isArray(stored.trayIds)
        ? stored.trayIds.filter((id, index, list) => productById[id] && poolIds.includes(id) && list.indexOf(id) === index)
        : [];
      if (restoredTray.length) {
        const fill = poolIds.filter((id) => !restoredTray.includes(id));
        state.trayIds = [...restoredTray, ...fill].slice(0, Math.min(TRAY_SIZE, poolIds.length));
      }
    }

    const reviewOfferCount = Number(params.get("sc_offer"));
    if (!fixtureMode && [3, 5].includes(reviewOfferCount)) {
      const reviewOrder = [pinnedId, ...allProductIds.filter((id) => id !== pinnedId)].slice(0, reviewOfferCount);
      state.quantities = Object.fromEntries(reviewOrder.map((id) => [id, 1]));
      state.order = reviewOrder;
      state.category = "all";
      state.trayIds = [pinnedId, ...allProductIds.filter((id) => id !== pinnedId)].slice(0, TRAY_SIZE);
    }

    if (!state.quantities[pinnedId]) state.quantities[pinnedId] = 1;

    const elements = {
      countCopy: root.querySelector("[data-sc-count-copy]"),
      countLeft: root.querySelector("[data-sc-count-left]"),
      progress: root.querySelector("[data-sc-progress]"),
      selected: root.querySelector("[data-sc-selected]"),
      offer: root.querySelector("[data-sc-offer]"),
      categories: root.querySelector("[data-sc-categories]"),
      tray: root.querySelector("[data-sc-tray]"),
      cartCount: root.querySelector("[data-sc-cart-count]"),
      cartThumb: root.querySelector("[data-sc-cart-thumb]"),
      footerLabel: root.querySelector("[data-sc-footer-label]"),
      footerPrice: root.querySelector("[data-sc-footer-price]"),
      addCart: root.querySelector("[data-sc-add-cart]"),
      shuffle: root.querySelector("[data-sc-shuffle]"),
      surprise: root.querySelector("[data-sc-surprise]"),
      toast: root.querySelector("[data-sc-toast]"),
      fixtureNote: root.querySelector("[data-sc-fixture-note]"),
      modal: root.querySelector("[data-sc-media-modal]"),
      modalStage: root.querySelector("[data-sc-modal-stage]"),
      modalClose: root.querySelector("[data-sc-modal-close]"),
      perfPanel: root.querySelector("[data-sc-perf-panel]"),
    };

    const perf = createPerformanceTracker(root, elements.perfPanel, {
      params,
      fixtureSize,
      productCount: products.length,
    });

    if (fixtureMode) {
      elements.fixtureNote.hidden = false;
      elements.fixtureNote.textContent = `${fixtureSize}-product performance fixture is active. Checkout is disabled in fixture mode.`;
    }

    const totalCount = () => Object.values(state.quantities).reduce((sum, quantity) => sum + quantity, 0);
    const money = (cents) => `$${(Number(cents) / 100).toFixed(2)}`;
    const playIconUrl = root.dataset.playIconUrl || "";
    const plusIconUrl = root.dataset.plusIconUrl || "";
    const giftIconUrl = root.dataset.giftIconUrl || "";

    const saveState = () => {
      if (fixtureMode) return;
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          pinnedId: state.pinnedId,
          quantities: state.quantities,
          order: state.order,
          category: state.category,
          trayIds: state.trayIds,
        }),
      );
    };

    let toastTimer;
    const showToast = (message) => {
      elements.toast.textContent = message;
      elements.toast.classList.add("is-visible");
      window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(() => elements.toast.classList.remove("is-visible"), 1800);
    };

    const nextTarget = (count) => MILESTONES.find((milestone) => milestone > count) || 8;
    const singlePriceCents = Number(variantByCount[1]?.priceCents || 1499);
    const boxPrice = (count) => Number(variantByCount[count]?.priceCents || 0);
    const totalSavings = (count) => money(count * singlePriceCents - boxPrice(count));
    const upgradeCost = (from, to) => boxPrice(to) - boxPrice(from);
    const upgradeSavings = (from, to) => money((to - from) * singlePriceCents - upgradeCost(from, to));
    const offerForCount = (count) => {
      if (count === 1) {
        return {
          kicker: "",
          title: "Pick one more squishy",
          copy: `Make it a 2-piece box for ${money(boxPrice(2))}. Your second pick costs only ${money(upgradeCost(1, 2))}.`,
          savings: `Save ${totalSavings(2)} vs. two singles`,
        };
      }
      if (count === 2) {
        return {
          kicker: "SWEET SPOT",
          title: `Your 3rd is only ${money(upgradeCost(2, 3))}`,
          copy: `Complete a 3-piece box for ${money(boxPrice(3))} — or checkout with 2 now.`,
          savings: `3 pieces · ${money(boxPrice(3))} total`,
        };
      }
      if (count === 3) {
        return {
          upgrade: true,
          upgradeLabel: "Unlock the 5-piece box",
          fromCount: 3,
          targetCount: 5,
          addCount: 2,
          upgradePrice: money(upgradeCost(3, 5)),
          savings: `Save ${upgradeSavings(3, 5)} vs. singles`,
        };
      }
      if (count === 4) {
        return {
          kicker: "ONE PICK FROM BIG MIX",
          title: `One more is only ${money(upgradeCost(4, 5))}`,
          copy: `Finish 5 for ${money(boxPrice(5))} and unlock the full Big Mix savings.`,
          savings: `5 pieces · save ${totalSavings(5)} total`,
        };
      }
      if (count === 5) {
        return {
          upgrade: true,
          upgradeLabel: "Unlock the 8-piece box",
          fromCount: 5,
          targetCount: 8,
          addCount: 3,
          upgradePrice: money(upgradeCost(5, 8)),
          savings: `Save ${upgradeSavings(5, 8)} vs. singles`,
        };
      }
      if (count === 6) {
        return {
          kicker: "ULTIMATE BOX",
          title: `Your next 2 are only ${money(upgradeCost(6, 8) / 2)} each`,
          copy: `Go from 6 to 8 pieces for just ${money(upgradeCost(6, 8))} more.`,
          savings: `8 pieces · ${money(boxPrice(8))} total`,
        };
      }
      if (count === 7) {
        return {
          kicker: "LAST PICK",
          title: `Your 8th is only ${money(upgradeCost(7, 8))}`,
          copy: "One final pick unlocks the full 8-piece value.",
          savings: `Save ${totalSavings(8)} vs. eight singles`,
        };
      }
      return {
        kicker: "HAPPY BOX COMPLETE",
        title: "You unlocked the best box price",
        copy: `Eight hand-picked squishies for ${money(boxPrice(8))}. You can still swap or adjust quantities before adding to cart.`,
        savings: `You saved ${totalSavings(8)} vs. singles`,
        complete: true,
      };
    };

    const renderProgress = () => {
      const count = totalCount();
      const target = nextTarget(count);
      elements.countCopy.textContent = count >= MAX_COMPONENTS ? "8 of 8 picked" : `${count} of ${target} picked`;
      elements.countLeft.textContent = count >= MAX_COMPONENTS ? "box full" : `${target - count} left`;
      elements.progress.innerHTML = MILESTONES.map((milestone) => {
        const complete = count >= milestone;
        const active = !complete && target === milestone;
        const variant = variantByCount[milestone];
        return `
          <div class="sc-milestone${complete ? " is-complete is-filled" : ""}${active ? " is-active" : ""}">
            <span class="sc-milestone-dot">${complete ? "✓" : milestone}</span>
            <small>${variant ? money(variant.priceCents) : milestone}</small>
          </div>`;
      }).join("");
    };

    const expandedSelection = () => {
      const result = [];
      for (const id of state.order) {
        for (let index = 0; index < (state.quantities[id] || 0); index += 1) {
          result.push({ id, pinnedUnit: id === state.pinnedId && index === 0 });
        }
      }
      return result;
    };

    const renderSelected = () => {
      const selected = expandedSelection();
      const slots = Array.from({ length: MAX_COMPONENTS }, (_, index) => {
        const item = selected[index];
        if (!item) return `<button class="sc-selected-empty" type="button" data-empty-slot="${index + 1}">PICK</button>`;
        const product = productById[item.id];
        const canRemove = !item.pinnedUnit || state.quantities[item.id] > 1;
        return `
          <button
            class="sc-selected-item${item.pinnedUnit ? " is-pinned" : ""}"
            type="button"
            data-selected-id="${escapeHtml(item.id)}"
            ${canRemove ? `aria-label="Remove one ${escapeHtml(product.name)}"` : `aria-label="Starting pick: ${escapeHtml(product.name)}"`}
          >
            <img src="${escapeHtml(product.image320)}" alt="${escapeHtml(product.imageAlt || product.name)}" loading="eager" decoding="async">
          </button>`;
      });
      elements.selected.innerHTML = slots.join("");
    };

    const renderOffer = () => {
      const offer = offerForCount(totalCount());
      elements.offer.className = `sc-offer-card${offer.upgrade ? " is-upgrade" : ""}${offer.complete ? " is-complete" : ""}`;

      if (offer.upgrade) {
        elements.offer.innerHTML = `
          <div class="sc-upgrade-ticket">
            <h1 class="sc-ticket-heading">
              ${giftIconUrl ? `<img src="${escapeHtml(giftIconUrl)}" alt="" aria-hidden="true" width="24" height="24">` : ""}
              <span>${escapeHtml(offer.upgradeLabel)}</span>
            </h1>
            <div
              class="sc-ticket-progress"
              role="progressbar"
              aria-label="${escapeHtml(`${offer.fromCount} picked out of ${offer.targetCount} pieces`)}"
              aria-valuemin="0"
              aria-valuemax="${offer.targetCount}"
              aria-valuenow="${offer.fromCount}"
            >
              <span class="sc-ticket-point is-current"><strong>${offer.fromCount}</strong><small>picked</small></span>
              <span class="sc-ticket-progress-line" aria-hidden="true"><i></i></span>
              <span class="sc-ticket-point is-target"><strong>${offer.targetCount}</strong><small>pieces</small></span>
            </div>
            <div class="sc-ticket-divider" aria-hidden="true"></div>
            <div class="sc-ticket-value">
              <strong><span>+${offer.addCount}</span> for <span>${escapeHtml(offer.upgradePrice)}</span></strong>
              <small>${escapeHtml(offer.savings)}</small>
            </div>
          </div>
        `;
        return;
      }

      elements.offer.innerHTML = `
        ${offer.kicker ? `<span class="sc-offer-kicker">${escapeHtml(offer.kicker)}</span>` : ""}
        <h1>${escapeHtml(offer.title)}</h1>
        <p>${escapeHtml(offer.copy)}</p>
        <span class="sc-offer-savings">${escapeHtml(offer.savings)}</span>
      `;
    };

    const renderCategories = () => {
      elements.categories.innerHTML = categories.map(
        (category) => `
          <button
            class="sc-category${state.category === category.key ? " is-active" : ""}"
            type="button"
            data-category="${escapeHtml(category.key)}"
            aria-pressed="${state.category === category.key}"
          >${escapeHtml(category.label)}</button>`,
      ).join("");
    };

    let modalState = null;
    let heroImageReady = false;
    const closeModal = () => {
      if (!modalState) return;
      const { video, originHost } = modalState;
      video.controls = false;
      if (originHost?.isConnected) {
        const reference = originHost.querySelector(".sc-media-play");
        originHost.insertBefore(video, reference || null);
      } else {
        video.remove();
      }
      elements.modal.hidden = true;
      elements.modal.setAttribute("aria-hidden", "true");
      document.body.classList.remove("sc-media-modal-open");
      modalState = null;
      perf.record("sc_media_modal_close");
    };

    const renderTray = () => {
      closeModal();
      const count = totalCount();
      elements.tray.innerHTML = state.trayIds.map((id, index) => {
        const product = productById[id];
        if (!product) return "";
        const quantity = state.quantities[product.id] || 0;
        const isPinnedMinimum = product.id === state.pinnedId && quantity <= 1;
        const actions = quantity
          ? `
            <div class="sc-stepper" aria-label="${escapeHtml(product.name)} quantity">
              <button type="button" data-action="remove" data-product-id="${escapeHtml(product.id)}" ${isPinnedMinimum ? "disabled" : ""} aria-label="Remove one ${escapeHtml(product.name)}">−</button>
              <span>${quantity}</span>
              <button type="button" data-action="add" data-product-id="${escapeHtml(product.id)}" ${count >= MAX_COMPONENTS ? "disabled" : ""} aria-label="Add another ${escapeHtml(product.name)}">
                <img class="sc-plus-icon" src="${escapeHtml(plusIconUrl)}" width="20" height="20" alt="" aria-hidden="true">
              </button>
            </div>`
          : `
            <button class="sc-add-one" type="button" data-action="add" data-product-id="${escapeHtml(product.id)}" ${count >= MAX_COMPONENTS ? "disabled" : ""} aria-label="Add ${escapeHtml(product.name)}">
              <img class="sc-plus-icon" src="${escapeHtml(plusIconUrl)}" width="20" height="20" alt="" aria-hidden="true">
            </button>`;
        const mediaPlayControl = product.video
          ? `
            <button class="sc-media-play" type="button" data-action="play-media" data-product-id="${escapeHtml(product.id)}" aria-label="Preview ${escapeHtml(product.name)} squeeze video" title="Preview video">
              <img src="${escapeHtml(playIconUrl)}" width="14" height="14" alt="" aria-hidden="true">
            </button>`
          : "";
        const mediaExpandControl = product.video
          ? `<button class="sc-media-expand-copy" type="button" data-action="expand-media" data-product-id="${escapeHtml(product.id)}" aria-label="Open ${escapeHtml(product.name)} video larger">View</button>`
          : "";
        const deferSecondaryImage = index > 0 && !heroImageReady;
        const sourceAttributes = deferSecondaryImage
          ? `src="${INLINE_IMAGE_PLACEHOLDER}" data-sc-src="${escapeHtml(product.image320)}"`
          : `src="${escapeHtml(product.image320)}"`;
        return `
          <article class="sc-product-card ${product.video ? "has-video" : "is-image-only"}${quantity ? " is-selected" : ""}" data-product-card="${escapeHtml(product.id)}" data-media-type="${product.video ? "video" : "image"}">
            <div class="sc-product-image" data-media-host="${escapeHtml(product.id)}">
              <img
                ${sourceAttributes}
                sizes="(min-width: 760px) 230px, calc((100vw - 58px) / 2)"
                alt="${escapeHtml(product.imageAlt || product.name)}"
                loading="${index === 0 ? "eager" : "lazy"}"
                decoding="async"
                ${index === 0 ? 'fetchpriority="high" data-sc-lcp-candidate' : 'fetchpriority="low"'}
              >
              ${mediaPlayControl}
              <span class="sc-picked-badge">IN BOX ×${quantity}</span>
            </div>
            <div class="sc-product-copy">
              <div>
                <div class="sc-product-heading">
                  <h3 title="${escapeHtml(product.name)}">${escapeHtml(product.cardName || product.name)}</h3>
                  ${mediaExpandControl}
                </div>
                <small class="sc-product-status ${quantity ? "is-selected" : "is-available"}">
                  ${quantity ? `In your box ×${quantity}` : "Tap + to choose"}
                </small>
              </div>
              <div class="sc-product-actions">${actions}</div>
            </div>
          </article>`;
      }).join("");
    };

    const hydrateSecondaryImages = () => {
      const deferredImages = elements.tray.querySelectorAll("img[data-sc-src]");
      for (const image of deferredImages) {
        const source = image.dataset.scSrc;
        const srcset = image.dataset.scSrcset;
        if (srcset) image.srcset = srcset;
        if (source) image.src = source;
        delete image.dataset.scSrc;
        delete image.dataset.scSrcset;
      }
      if (deferredImages.length) {
        perf.record("sc_secondary_images_released", { count: deferredImages.length });
      }
    };

    const renderFooter = () => {
      const count = totalCount();
      const variant = variantByCount[count];
      const pinned = productById[state.pinnedId];
      elements.footerLabel.textContent = `${count}-piece total`;
      elements.footerPrice.textContent = variant ? money(variant.priceCents) : "—";
      elements.addCart.querySelector("span").textContent = fixtureMode
        ? "Fixture mode — cart off"
        : `Add ${count} to cart`;
      elements.addCart.disabled = fixtureMode || !variant;
      elements.cartThumb.innerHTML = `<img src="${escapeHtml(pinned.image320)}" alt=""><b>${count}</b>`;
    };

    const render = () => {
      renderProgress();
      renderSelected();
      renderOffer();
      renderCategories();
      renderTray();
      renderFooter();
      saveState();
    };

    const imageCache = new Map();
    const preloadImage = (product, signal) => {
      const key = product.image320;
      if (imageCache.has(key)) return imageCache.get(key);
      const promise = new Promise((resolve, reject) => {
        const image = new Image();
        let settled = false;
        const finish = (callback, value) => {
          if (settled) return;
          settled = true;
          signal?.removeEventListener("abort", abort);
          callback(value);
        };
        const abort = () => {
          image.src = "";
          imageCache.delete(key);
          finish(reject, new DOMException("Image preload cancelled", "AbortError"));
        };
        if (signal?.aborted) {
          abort();
          return;
        }
        signal?.addEventListener("abort", abort, { once: true });
        image.decoding = "async";
        image.onload = async () => {
          try {
            await image.decode();
          } catch {
            // A loaded image remains usable if decode() is unavailable or rejects.
          }
          finish(resolve, product.id);
        };
        image.onerror = () => {
          imageCache.delete(key);
          finish(reject, new Error(`Could not preload ${product.name}`));
        };
        image.src = product.image320;
      });
      imageCache.set(key, promise);
      return promise;
    };

    const preloadTray = (ids, signal) =>
      Promise.allSettled(ids.map((id) => productById[id]).filter(Boolean).map((product) => preloadImage(product, signal)));

    const trayQueues = new Map();
    const chooseNextTray = (poolIds, previousIds) => {
      if (!poolIds.length) return [];
      const previous = new Set(previousIds);
      const fresh = shuffled(poolIds.filter((id) => !previous.has(id)));
      const repeats = shuffled(poolIds.filter((id) => previous.has(id)));
      return [...fresh, ...repeats].slice(0, Math.min(TRAY_SIZE, poolIds.length));
    };

    const ensureQueue = (key) => {
      const poolIds = categoryPoolIds(key);
      const queue = trayQueues.get(key) || [];
      while (queue.length < 2) {
        const previous = queue.at(-1) || state.trayIds;
        queue.push(chooseNextTray(poolIds, previous));
      }
      trayQueues.set(key, queue);
      return queue;
    };

    let prefetchController;
    const prefetchDepth = () => {
      const connection = navigator.connection;
      if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || "")) return 0;
      if (connection?.effectiveType === "3g") return TRAY_SIZE;
      return TRAY_SIZE * 2;
    };
    const scheduleQueuePrefetch = () => {
      prefetchController?.abort();
      if (!heroImageReady) return;
      const depth = prefetchDepth();
      if (!depth) return;
      prefetchController = new AbortController();
      const ids = ensureQueue(state.category).flat().slice(0, depth);
      const run = () => preloadTray(ids, prefetchController.signal);
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(run, { timeout: 1200 });
      } else {
        window.setTimeout(run, 120);
      }
    };

    const addOne = (id) => {
      if (!productById[id]) return;
      if (totalCount() >= MAX_COMPONENTS) {
        showToast("Your 8-piece box is full");
        return;
      }
      if (!state.quantities[id]) state.order.push(id);
      state.quantities[id] = (state.quantities[id] || 0) + 1;
      render();
    };

    const removeOne = (id) => {
      if (!state.quantities[id]) return;
      if (id === state.pinnedId && state.quantities[id] <= 1) {
        showToast("Your Meta ad pick stays as the first item");
        return;
      }
      state.quantities[id] -= 1;
      if (state.quantities[id] <= 0) {
        delete state.quantities[id];
        state.order = state.order.filter((item) => item !== id);
      }
      render();
    };

    let shuffleRunning = false;
    const freshTray = async () => {
      if (shuffleRunning) return;
      shuffleRunning = true;
      const start = performance.now();
      perf.record("sc_shuffle_click", { category: state.category });
      elements.shuffle.classList.add("is-loading");
      elements.shuffle.disabled = true;
      try {
        const queue = ensureQueue(state.category);
        const next = queue.shift() || chooseNextTray(categoryPoolIds(state.category), state.trayIds);
        await preloadTray(next);
        state.trayIds = next;
        renderTray();
        saveState();
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        perf.markShuffle(performance.now() - start);
        showToast("Fresh tray ready — your picks stayed saved");
        ensureQueue(state.category);
        scheduleQueuePrefetch();
      } finally {
        elements.shuffle.classList.remove("is-loading");
        elements.shuffle.disabled = false;
        shuffleRunning = false;
      }
    };

    const surprise = () => {
      const candidates = state.trayIds.map((id) => productById[id]).filter(Boolean);
      if (!candidates.length) return;
      const product = candidates[Math.floor(Math.random() * candidates.length)];
      addOne(product.id);
      showToast(`${product.name} joined your box`);
    };

    const startInlineVideo = async (productId, host, expandAfter = false) => {
      const product = productById[productId];
      if (!product?.video || !host) return null;
      const existingVideo = host.querySelector("video.sc-inline-video");
      if (existingVideo) {
        if (existingVideo.paused) {
          await existingVideo.play().catch(() => {});
          host.classList.add("is-playing");
          host.classList.remove("is-paused");
        }
        if (expandAfter) openModal(existingVideo, host, productId);
        return existingVideo;
      }

      const playButton = host.querySelector(".sc-media-play");
      playButton?.classList.add("is-loading");
      const start = performance.now();
      perf.record("sc_media_click", { productId, mode: expandAfter ? "modal" : "inline" });

      const video = document.createElement("video");
      video.className = "sc-inline-video";
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.preload = "none";
      video.setAttribute("muted", "");
      video.setAttribute("playsinline", "");
      video.setAttribute("aria-label", `${product.name} squeeze demonstration`);
      if (product.videoPoster) video.poster = product.videoPoster;

      const ready = new Promise((resolve, reject) => {
        video.addEventListener(
          "loadeddata",
          () => {
            const duration = performance.now() - start;
            playButton?.classList.remove("is-loading");
            host.classList.add("is-playing");
            host.classList.remove("is-paused");
            perf.markVideoFirstFrame(duration, productId);
            resolve(video);
          },
          { once: true },
        );
        video.addEventListener(
          "error",
          () => {
            playButton?.classList.remove("is-loading");
            showToast("The video could not load. The product image is still available.");
            perf.record("sc_media_error", { productId });
            reject(new Error(`Video failed for ${product.name}`));
          },
          { once: true },
        );
      });

      video.addEventListener("click", () => {
        if (video.paused) {
          video.play().catch(() => {});
          host.classList.add("is-playing");
          host.classList.remove("is-paused");
        } else {
          video.pause();
          host.classList.remove("is-playing");
          host.classList.add("is-paused");
        }
      });

      host.insertBefore(video, playButton || null);
      video.src = product.video;
      video.load();
      video.play().catch(() => {});

      try {
        await ready;
        if (expandAfter) openModal(video, host, productId);
        return video;
      } catch {
        video.remove();
        return null;
      }
    };

    function openModal(video, originHost, productId) {
      if (!video || !originHost || modalState) return;
      modalState = { video, originHost, productId };
      video.controls = true;
      elements.modalStage.append(video);
      elements.modal.hidden = false;
      elements.modal.setAttribute("aria-hidden", "false");
      document.body.classList.add("sc-media-modal-open");
      perf.record("sc_media_modal_open", { productId });
      elements.modalClose.focus({ preventScroll: true });
    }

    const updateCartCount = async () => {
      try {
        const response = await fetchCartSnapshot(root.dataset.cartJsonUrl);
        if (!response.ok) return;
        const cart = await readShopifyJson(response, "Could not read your current cart");
        elements.cartCount.textContent = String(cart.item_count || 0);
      } catch {
        // Cart count is useful but never blocks the picker.
      }
    };

    const isBoxCartItem = (item) =>
      /^SC-BOX-[1-8]$/.test(String(item?.sku || "")) ||
      bundle.variants.some((variant) => String(variant.id) === String(item?.variant_id || item?.id));

    const replacePreviousBuilderBoxes = async () => {
      const cartResponse = await fetchCartSnapshot(root.dataset.cartJsonUrl);
      if (!cartResponse.ok) {
        throw new Error(
          cartResponse.status === 429
            ? "Shopify is briefly busy. Please wait a moment and try again."
            : "Could not check your current cart",
        );
      }
      const cart = await readShopifyJson(cartResponse, "Could not read your current cart");
      const updates = Object.fromEntries(
        (cart.items || []).filter(isBoxCartItem).map((item) => [item.key, 0]),
      );
      if (!Object.keys(updates).length) return;
      const updateResponse = await fetchWithTimeout(root.dataset.cartUpdateUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates }),
      }, 10_000);
      const payload = await readShopifyJson(updateResponse, "Could not replace your previous box");
      if (!updateResponse.ok) {
        throw new Error(payload.description || payload.message || "Could not replace your previous box");
      }
    };

    const addBundleToCart = async () => {
      if (fixtureMode) {
        showToast("Checkout is disabled in catalog fixture mode");
        return;
      }
      const count = totalCount();
      const variant = variantByCount[count];
      if (!variant || elements.addCart.disabled) return;

      const selectionPayload = state.order
        .filter((id) => state.quantities[id])
        .map((id) => ({
          variantId: String(productById[id].variantId).startsWith("gid://")
            ? productById[id].variantId
            : `gid://shopify/ProductVariant/${productById[id].variantId}`,
          sku: productById[id].sku,
          quantity: state.quantities[id],
        }));
      const componentProperties = Object.fromEntries(
        selectionPayload.map((component, index) => [
          `_sc_component_${index + 1}`,
          `${component.variantId}|${component.quantity}`,
        ]),
      );

      elements.addCart.disabled = true;
      const label = elements.addCart.querySelector("span");
      const originalLabel = label.textContent;
      label.textContent = "Adding your box…";
      try {
        await replacePreviousBuilderBoxes();
        const response = await fetchWithTimeout(root.dataset.cartAddUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            items: [
              {
                id: variant.id,
                quantity: 1,
                properties: {
                  ...componentProperties,
                  _sc_builder: "squish-club-v2",
                  _sc_ad_pick: productById[state.pinnedId].sku,
                },
              },
            ],
          }),
        }, 12_000);
        const payload = await readShopifyJson(response, "Could not add this box. Please try again.");
        if (!response.ok) throw new Error(payload.description || payload.message || "Could not add this box");
        await updateCartCount();
        label.textContent = "Box added!";
        window.setTimeout(() => window.location.assign(root.dataset.cartUrl), 450);
      } catch (error) {
        showToast(error.message || "Could not add this box. Please try again.");
        label.textContent = originalLabel;
        elements.addCart.disabled = false;
      }
    };

    elements.tray.addEventListener("click", async (event) => {
      const button = event.target.closest("button[data-action]");
      if (!button) return;
      const id = button.dataset.productId;
      if (button.dataset.action === "add") addOne(id);
      if (button.dataset.action === "remove") removeOne(id);
      if (button.dataset.action === "play-media") {
        const host = button.closest("[data-media-host]");
        await startInlineVideo(id, host, false);
      }
      if (button.dataset.action === "expand-media") {
        const host = button.closest("[data-media-host]") || button.closest("[data-product-card]")?.querySelector("[data-media-host]");
        const video = host?.querySelector("video.sc-inline-video");
        if (video) openModal(video, host, id);
        else await startInlineVideo(id, host, true);
      }
    });

    elements.selected.addEventListener("click", (event) => {
      const selectedButton = event.target.closest("[data-selected-id]");
      if (selectedButton) removeOne(selectedButton.dataset.selectedId);
      if (event.target.closest("[data-empty-slot]")) {
        elements.tray.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });

    elements.categories.addEventListener("click", (event) => {
      const button = event.target.closest("[data-category]");
      if (!button) return;
      const nextCategory = button.dataset.category;
      if (!validCategoryKeys.has(nextCategory) || nextCategory === state.category) return;
      prefetchController?.abort();
      state.category = nextCategory;
      const poolIds = categoryPoolIds(nextCategory);
      state.trayIds = shuffled(poolIds).slice(0, Math.min(TRAY_SIZE, poolIds.length));
      trayQueues.delete(nextCategory);
      renderCategories();
      renderTray();
      saveState();
      perf.record("sc_category_rendered", { category: nextCategory, products: poolIds.length });
      ensureQueue(nextCategory);
      scheduleQueuePrefetch();
    });

    elements.shuffle.addEventListener("click", freshTray);
    elements.surprise.addEventListener("click", surprise);
    elements.addCart.addEventListener("click", addBundleToCart);
    elements.modalClose.addEventListener("click", closeModal);
    elements.modal.addEventListener("click", (event) => {
      if (event.target === elements.modal) closeModal();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && modalState) closeModal();
    });

    render();
    root.dataset.scPerfReady = "true";
    root.dataset.scInteractive = "true";
    root.setAttribute("aria-busy", "false");
    delete root.dataset.scPerfLoading;
    perf.record("sc_initial_render", { trayIds: [...state.trayIds] });

    const firstImage = elements.tray.querySelector("[data-sc-lcp-candidate]");
    let firstImageMarked = false;
    const markFirstImage = () => {
      if (firstImageMarked) return;
      firstImageMarked = true;
      heroImageReady = true;
      perf.record("sc_first_card_image_loaded", { productId: state.trayIds[0] });
      hydrateSecondaryImages();
      ensureQueue(state.category);
      scheduleQueuePrefetch();
    };
    elements.tray.addEventListener(
      "load",
      (event) => {
        if (event.target.matches?.("[data-sc-lcp-candidate]")) markFirstImage();
      },
      true,
    );
    if (firstImage?.complete && firstImage.naturalWidth) markFirstImage();
    else firstImage?.addEventListener("load", markFirstImage, { once: true });
    window.setTimeout(() => {
      if (heroImageReady) return;
      heroImageReady = true;
      hydrateSecondaryImages();
      scheduleQueuePrefetch();
      perf.record("sc_secondary_images_timeout_release");
    }, 3500);

    updateCartCount();
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        perf.markPickerReady();
        ensureQueue(state.category);
        if (heroImageReady) scheduleQueuePrefetch();
      }),
    );
  }

  const initializeAll = (scope = document) => {
    scope.querySelectorAll?.(".sc-perf-builder").forEach(initializeBuilder);
  };

  initializeAll();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll(), { once: true });
  }
  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
})();
